import React, { useState, useRef } from 'react';
import './Login.css';

// Самостоятельная регистрация компании существует только на публичном SaaS-домене.
// На выделенных инстансах (поддомен клиента вроде alessa.pilatalisa.ru, ИПК) сам
// эндпоинт /api/auth/register теперь физически не зарегистрирован на бэкенде
// (см. main.py) — здесь просто не показываем ведущую в никуда вкладку.
// До входа профиль компании ещё не известен, поэтому единственный доступный
// сигнал — хост, с которого открыта страница (не build-time переменная: один и
// тот же собранный бандл обслуживает все хосты сразу).
const PUBLIC_SIGNUP_HOSTS = ['login.ai-matcher.ru', 'localhost', '127.0.0.1'];

function Login({ onLogin }) {
  const customerDeployment = !PUBLIC_SIGNUP_HOSTS.includes(window.location.hostname);
  const initialMode = !customerDeployment && new URLSearchParams(window.location.search).get('register') !== null ? 'register' : 'login';
  const [mode, setMode] = useState(initialMode); // 'login' | 'register'
  // Синхронный флаг против двойной отправки: React обновляет disabled на кнопке
  // только на следующий рендер, а быстрый двойной клик/Enter успевает пройти
  // раньше — тогда второй запрос на регистрацию с уже занятым email прилетает
  // после того, как первый уже создал пользователя, и человек видит ошибку
  // "уже существует", хотя регистрация на самом деле прошла.
  const submittingRef = useRef(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '';

  const extractError = (data, fallback) => {
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail) && data.detail.length > 0) {
      return data.detail[0].msg || data.detail[0].message || fallback;
    }
    return fallback;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setError('');
    setLoading(true);

    if (mode === 'register' && !agreeToTerms) {
      setError('Необходимо принять условия пользовательского соглашения');
      setLoading(false);
      submittingRef.current = false;
      return;
    }

    const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
    const body = mode === 'login'
      ? { email, password }
      : { email, password, company_name: companyName, agree_to_terms: agreeToTerms };

    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(extractError(data, mode === 'login' ? 'Ошибка при входе' : 'Ошибка при регистрации'));
        return;
      }

      if (mode === 'register' && typeof window.ym === 'function') {
        window.ym(111850791, 'reachGoal', 'registr');
      }

      onLogin(data.access_token, data.user_id, data.role, data.company_id, data.onboarding_status, data.ui_profile);
    } catch (err) {
      setError('Ошибка подключения к серверу');
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>ИИ:Матчер</h1>
          <p>Автоматическое сопоставление номенклатуры</p>
        </div>

        <div className="login-tabs">
          <button
            type="button"
            className={`tab ${mode === 'login' ? 'active' : ''}`}
            onClick={() => switchMode('login')}
          >
            Вход
          </button>
          {!customerDeployment && (
            <button
              type="button"
              className={`tab ${mode === 'register' ? 'active' : ''}`}
              onClick={() => switchMode('register')}
            >
              Регистрация компании
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {mode === 'register' && (
            <div className="login-field">
              <label>Название компании</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="ООО «Ромашка»"
                required
              />
            </div>
          )}

          <div className="login-field">
            <label>{mode === 'login' ? 'Логин' : 'Email'}</label>
            <input
              type={mode === 'login' ? 'text' : 'email'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={mode === 'login' ? 'Введите логин' : 'you@company.com'}
              required
            />
          </div>

          <div className="login-field">
            <label>Пароль</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
            {mode === 'register' && (
              <small className="field-hint">Минимум 6 символов, латиница, минимум одна заглавная буква и одна цифра</small>
            )}
          </div>

          {mode === 'register' && (
            <div className="login-field agree-group">
              <label className="agree-label">
                <input
                  type="checkbox"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                />
                <span>
                  Я принимаю условия{' '}
                  <a href="/legal/terms.html" target="_blank" rel="noreferrer">пользовательского соглашения</a>
                  {' '}и{' '}
                  <a href="/legal/privacy.html" target="_blank" rel="noreferrer">политики обработки персональных данных</a>
                </span>
              </label>
            </div>
          )}

          {error && <div className="login-error">{error}</div>}

          <button type="submit" className="login-submit" disabled={loading || (mode === 'register' && !agreeToTerms)}>
            {loading
              ? 'Загрузка...'
              : mode === 'login' ? 'Вход' : 'Создать компанию'}
          </button>
        </form>

        {mode === 'register' && (
          <p className="login-footnote">
            30 дней без абонентской платы — оплачиваются только токены GPT по вашему ключу OpenAI.
            Вы станете администратором новой компании и сможете добавить остальных пользователей из настроек.
          </p>
        )}
      </div>
    </div>
  );
}

export default Login;
